import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { dashboardSummary, weeklyReport } from "@/lib/api";
import { formatINR, todayISO } from "@/lib/format";
import { useBranch } from "@/context/BranchContext";
import KpiCard from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Wallet, TrendingUp, HandCoins, AlertTriangle, Plus, ArrowUpRight, Banknote, Smartphone, Landmark, MoreHorizontal, CalendarRange, MessageCircle } from "lucide-react";
import { waLink, buildDailySummaryMessage } from "@/lib/whatsapp";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { branchId } = useBranch();

  const load = async (d) => {
    setLoading(true);
    try {
      const [res, wk] = await Promise.all([
        dashboardSummary(d),
        weeklyReport({ weeks: 4, end_date: d }),
      ]);
      setData(res);
      setWeekly(wk);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, branchId]);

  const t = data?.today || {};
  const at = data?.all_time || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Overview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Daily Collection Ledger
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Track cash, deductions, payables and balances per delivery boy — every day.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-widest text-zinc-500">Date</label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-40"
              data-testid="dashboard-date-input"
            />
          </div>
          <a
            href={waLink("", buildDailySummaryMessage(data))}
            target="_blank"
            rel="noreferrer"
            title="Send daily summary on WhatsApp"
          >
            <Button
              variant="outline"
              className="rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              data-testid="dashboard-wa-share-btn"
            >
              <MessageCircle className="h-4 w-4 mr-1.5" /> Send on WhatsApp
            </Button>
          </a>
          <Button
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg"
            onClick={() => navigate("/collections/new")}
            data-testid="dashboard-new-entry-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New Entry
          </Button>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <KpiCard
          label="Cash Collected"
          value={t.cash_collected}
          sub={`${t.count || 0} entries today`}
          tone="default"
          icon={Wallet}
          testId="kpi-cash-collected"
        />
        <KpiCard
          label="Final Payable"
          value={t.final_payable}
          sub="After all deductions"
          tone="default"
          icon={TrendingUp}
          testId="kpi-final-payable"
        />
        <KpiCard
          label="Paid Today"
          value={t.paid_amount}
          sub="Cash / UPI / Bank"
          tone="positive"
          icon={HandCoins}
          testId="kpi-paid-today"
        />
        <KpiCard
          label="Balance Due"
          value={t.balance}
          sub={t.balance > 0 ? "Awaiting settlement" : "All settled"}
          tone={t.balance > 0 ? "warning" : "positive"}
          icon={AlertTriangle}
          testId="kpi-balance-due"
        />
      </div>

      {/* Secondary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatMini label="Delivery Boys" value={data?.total_delivery_boys ?? 0} isCurrency={false} testId="stat-boys" />
        <StatMini label="Earnings Paid" value={t.earning} testId="stat-earnings" />
        <StatMini label="Shop Deducted" value={t.shop_deduction} testId="stat-shop-ded" />
        <StatMini label="Min Guarantee Applied" value={t.min_guarantee_applied} tone="danger" testId="stat-min-guarantee" />
      </div>

      {/* Payment methods breakdown - today */}
      <section className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="pm-breakdown-today">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-medium tracking-tight">Payment Received — Today</h2>
            <p className="text-xs text-zinc-500 mt-0.5">How today&apos;s paid amount was received across methods.</p>
          </div>
          <div className="text-right tabular">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Paid Today</div>
            <div className="font-display text-lg font-semibold text-emerald-700">{formatINR(t.paid_amount || 0)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PmCard label="Cash" value={(t.by_payment_method || {}).cash} total={t.paid_amount} icon={Banknote} tint="emerald" testId="pm-cash-today" />
          <PmCard label="UPI" value={(t.by_payment_method || {}).upi} total={t.paid_amount} icon={Smartphone} tint="blue" testId="pm-upi-today" />
          <PmCard label="Bank" value={(t.by_payment_method || {}).bank} total={t.paid_amount} icon={Landmark} tint="violet" testId="pm-bank-today" />
          <PmCard label="Other" value={(t.by_payment_method || {}).other} total={t.paid_amount} icon={MoreHorizontal} tint="zinc" testId="pm-other-today" />
        </div>
      </section>

      {/* Payment methods breakdown - all time */}
      <section className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="pm-breakdown-all">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-medium tracking-tight">Payment Received — All Time</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Lifetime breakdown of payments across methods.</p>
          </div>
          <div className="text-right tabular">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Received</div>
            <div className="font-display text-lg font-semibold text-emerald-700">{formatINR(at.paid_amount || 0)}</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PmCard label="Cash" value={(at.by_payment_method || {}).cash} total={at.paid_amount} icon={Banknote} tint="emerald" testId="pm-cash-all" />
          <PmCard label="UPI" value={(at.by_payment_method || {}).upi} total={at.paid_amount} icon={Smartphone} tint="blue" testId="pm-upi-all" />
          <PmCard label="Bank" value={(at.by_payment_method || {}).bank} total={at.paid_amount} icon={Landmark} tint="violet" testId="pm-bank-all" />
          <PmCard label="Other" value={(at.by_payment_method || {}).other} total={at.paid_amount} icon={MoreHorizontal} tint="zinc" testId="pm-other-all" />
        </div>
      </section>

      {/* Weekly details */}
      <section className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="dashboard-weekly">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-display text-xl font-medium tracking-tight inline-flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-emerald-700" /> This Week & Last 4 Weeks
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">Mon–Sun weekly snapshot. Click a week for full breakdown.</p>
          </div>
          <Link to="/weekly">
            <Button variant="outline" size="sm" data-testid="dashboard-weekly-view-all">
              Open Weekly Report <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </Link>
        </div>

        {weekly?.weeks?.length > 0 && (
          <div className="space-y-3">
            {/* Current week highlight */}
            {(() => {
              const cur = weekly.weeks[0];
              return (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Current Week</div>
                      <div className="font-display text-lg font-semibold text-zinc-950">{cur.label}</div>
                      <div className="text-xs text-zinc-500 tabular">{cur.entries} {cur.entries === 1 ? "entry" : "entries"}</div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 tabular">
                      <WkStat label="Cash" value={cur.cash_collected} />
                      <WkStat label="Payable" value={cur.final_payable} />
                      <WkStat label="Paid" value={cur.paid_amount} tone="positive" />
                      <WkStat label="Balance" value={cur.balance} tone={cur.balance > 0 ? "warning" : "default"} />
                      <WkStat label="MG" value={cur.min_guarantee_applied} tone="danger" />
                    </div>
                  </div>
                  {/* PM row for current week */}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 tabular">
                    <MiniPm label="Cash" value={(cur.by_payment_method || {}).cash} tint="text-emerald-700 bg-emerald-100/60 border-emerald-200" />
                    <MiniPm label="UPI" value={(cur.by_payment_method || {}).upi} tint="text-blue-700 bg-blue-50 border-blue-200" />
                    <MiniPm label="Bank" value={(cur.by_payment_method || {}).bank} tint="text-violet-700 bg-violet-50 border-violet-200" />
                    <MiniPm label="Other" value={(cur.by_payment_method || {}).other} tint="text-zinc-700 bg-zinc-100 border-zinc-200" />
                  </div>
                </div>
              );
            })()}

            {/* Previous weeks compact table */}
            <div className="overflow-x-auto rounded-lg border border-zinc-200">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left">
                    <Th>Week</Th>
                    <Th className="text-right">Entries</Th>
                    <Th className="text-right">Cash</Th>
                    <Th className="text-right">Payable</Th>
                    <Th className="text-right">Paid</Th>
                    <Th className="text-right">Balance</Th>
                    <Th className="text-right">MG</Th>
                  </tr>
                </thead>
                <tbody>
                  {weekly.weeks.slice(1).map((w) => (
                    <tr key={w.week_start} className="border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 tabular" data-testid={`dashboard-week-${w.week_start}`}>
                      <td className="px-4 py-2.5 font-medium">{w.label}</td>
                      <td className="px-4 py-2.5 text-right">{w.entries}</td>
                      <td className="px-4 py-2.5 text-right">{formatINR(w.cash_collected)}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{formatINR(w.final_payable)}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-700">{formatINR(w.paid_amount)}</td>
                      <td className={`px-4 py-2.5 text-right font-semibold ${w.balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                        {formatINR(w.balance)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-rose-600">{formatINR(w.min_guarantee_applied)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shortfalls */}
        <section className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="shortfalls-section">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-medium tracking-tight">Minimum Guarantee Shortfalls</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Delivery boys whose earning today fell below guarantee.</p>
            </div>
            <Badge variant="outline" className="border-rose-200 text-rose-600 bg-rose-50">
              {data?.shortfalls_today?.length || 0} flagged
            </Badge>
          </div>
          <div className="mt-4 divide-y divide-zinc-100">
            {(data?.shortfalls_today || []).length === 0 && (
              <div className="text-sm text-zinc-500 py-6 text-center">
                No shortfalls today. Everyone hit their guarantee.
              </div>
            )}
            {(data?.shortfalls_today || []).map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium">{s.delivery_boy_name || "—"}</div>
                  <div className="text-xs text-zinc-500 tabular">Earning {formatINR(s.earning)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Shortfall</div>
                  <div className="font-display text-lg font-semibold text-rose-600 tabular">
                    {formatINR(s.min_guarantee_applied)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Outstanding balances */}
        <section className="bg-white rounded-xl border border-zinc-200 p-6" data-testid="outstanding-section">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-medium tracking-tight">Outstanding Balances</h2>
              <p className="text-xs text-zinc-500 mt-0.5">All-time unsettled amounts, largest first.</p>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Total</div>
              <div className="font-display text-lg font-semibold text-amber-700 tabular">
                {formatINR(data?.outstanding_total || 0)}
              </div>
            </div>
          </div>
          <div className="mt-4 divide-y divide-zinc-100">
            {(data?.outstanding_top || []).length === 0 && (
              <div className="text-sm text-zinc-500 py-6 text-center">No outstanding balances.</div>
            )}
            {(data?.outstanding_top || []).map((o) => (
              <button
                key={o.id}
                onClick={() => navigate(`/collections/${o.id}`)}
                className="w-full flex items-center justify-between py-3 text-left hover:bg-zinc-50 rounded-md px-2 -mx-2 transition-colors"
                data-testid={`outstanding-row-${o.id}`}
              >
                <div>
                  <div className="text-sm font-medium">{o.delivery_boy_name || "—"}</div>
                  <div className="text-xs text-zinc-500 tabular">{o.entry_date}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-base font-semibold text-amber-700 tabular">
                    {formatINR(o.balance)}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* All-time strip */}
      <section className="bg-white rounded-xl border border-zinc-200 p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-medium tracking-tight">All-Time Totals</h2>
          <div className="text-xs text-zinc-500">{at.count || 0} entries recorded</div>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 tabular">
          <StatMini label="Cash Collected" value={at.cash_collected} testId="all-cash" />
          <StatMini label="Final Payable" value={at.final_payable} testId="all-payable" />
          <StatMini label="Paid" value={at.paid_amount} tone="positive" testId="all-paid" />
          <StatMini label="Balance" value={at.balance} tone="warning" testId="all-balance" />
        </div>
      </section>

      {loading && !data && (
        <div className="text-sm text-zinc-500 text-center py-8">Loading…</div>
      )}
    </div>
  );
}

const StatMini = ({ label, value, tone = "default", isCurrency = true, testId }) => {
  const cls = {
    default: "text-zinc-900",
    positive: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-rose-600",
  }[tone];
  return (
    <div className="bg-zinc-50 border border-zinc-100 rounded-lg p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1.5 font-display text-xl font-semibold tabular ${cls}`}>
        {isCurrency ? formatINR(value) : value}
      </div>
    </div>
  );
};

const PmCard = ({ label, value, total, icon: Icon, tint, testId }) => {
  const v = value || 0;
  const pct = total > 0 ? Math.round((v / total) * 100) : 0;
  const tints = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", bar: "bg-emerald-600" },
    blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", bar: "bg-blue-600" },
    violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", bar: "bg-violet-600" },
    zinc: { bg: "bg-zinc-50", border: "border-zinc-200", text: "text-zinc-700", bar: "bg-zinc-600" },
  }[tint] || {};
  return (
    <div className={`rounded-lg border ${tints.border} ${tints.bg} p-4`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className={`text-[11px] uppercase tracking-widest font-semibold ${tints.text}`}>{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${tints.text}`} />}
      </div>
      <div className={`mt-2 font-display text-2xl font-semibold tabular ${tints.text}`}>{formatINR(v)}</div>
      <div className="mt-2 h-1.5 rounded-full bg-white/60 overflow-hidden">
        <div className={`${tints.bar} h-full rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] tabular text-zinc-500">{pct}% of received</div>
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);

const WkStat = ({ label, value, tone = "default" }) => {
  const cls = {
    default: "text-zinc-900",
    positive: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-rose-600",
  }[tone];
  return (
    <div className="text-right md:text-left">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-base font-semibold ${cls}`}>{formatINR(value || 0)}</div>
    </div>
  );
};

const MiniPm = ({ label, value, tint }) => (
  <div className={`rounded-md border px-3 py-1.5 flex items-center justify-between ${tint}`}>
    <span className="text-[10px] uppercase tracking-widest font-semibold">{label}</span>
    <span className="font-display text-sm font-semibold tabular">{formatINR(value || 0)}</span>
  </div>
);
